import { ChangeDetectorRef, Component, OnInit, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { RoleListItem } from "../../Dto/RoleModel";
import { Permission } from "../../../Permission/Dto/PermissionModel";
import { PermissionEffects } from "../../../Permission/State/PermissionEffect";
import { PermissionState } from "../../../Permission/State/PermissionState";
import { RoleEffects } from "../../State/RoleEffect";
import { RoleState } from "../../State/RoleState";
import { PermissionGroup } from "../../Dto/RolePageModel";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { snakeCaseToTitle } from "../../../../Common/Utility/UtilityString";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";

@Component({
  selector: "app-role-edit",
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: "./RoleEditPage.html",
  styleUrl: "./RoleEditPage.scss",
})
export class RoleEditPage implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly permissionEffects = inject(PermissionEffects);
  readonly permissionState = inject(PermissionState);
  private readonly roleEffects = inject(RoleEffects);
  private readonly roleState = inject(RoleState);
  private readonly toastEffects = inject(ToastEffects);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly permissionSearch = new FormControl("", { nonNullable: true });
  readonly selectedPermissionIds = new Set<string>();
  readonly roleId = this.route.snapshot.paramMap.get("id") ?? undefined;

  role: RoleListItem | null = null;
  isSaving = false;
  errorMessage = "";

  ngOnInit(): void {
    this.resolveRoleFromNavigation();
    this.permissionEffects.fetchPermissions().catch(() => undefined);
  }

  get permissionGroups(): PermissionGroup[] {
    const term = this.permissionSearch.value.trim().toLowerCase();
    const grouped = new Map<string, Permission[]>();

    for (const permission of this.permissionState.data()) {
      if (term && !this.matchesSearch(permission, term)) {
        continue;
      }

      const existing = grouped.get(permission.module) ?? [];
      existing.push(permission);
      grouped.set(permission.module, existing);
    }

    return Array.from(grouped.entries()).map(([module, permissions]) => ({
      module,
      permissions,
    }));
  }

  snakeCaseToTitle = snakeCaseToTitle;

  isPermissionSelected(permissionId: string): boolean {
    return this.selectedPermissionIds.has(permissionId);
  }

  togglePermission(permissionId: string): void {
    if (this.selectedPermissionIds.has(permissionId)) {
      this.selectedPermissionIds.delete(permissionId);
    } else {
      this.selectedPermissionIds.add(permissionId);
    }
  }

  areAllModulePermissionsSelected(moduleName: string): boolean {
    const permissions = this.getModulePermissions(moduleName);
    if (permissions.length === 0) {
      return false;
    }

    return permissions.every((permission) => this.selectedPermissionIds.has(permission.id));
  }

  toggleModulePermissions(moduleName: string): void {
    const permissions = this.getModulePermissions(moduleName);
    const shouldSelectAll = !this.areAllModulePermissionsSelected(moduleName);

    for (const permission of permissions) {
      if (shouldSelectAll) {
        this.selectedPermissionIds.add(permission.id);
      } else {
        this.selectedPermissionIds.delete(permission.id);
      }
    }
  }

  selectAllPermissions(): void {
    // Get all permissions that match the current search term
    const term = this.permissionSearch.value.trim().toLowerCase();
    const allPermissions = this.permissionState.data().filter((permission) => {
      if (term) {
        return this.matchesSearch(permission, term);
      }
      return true;
    });

    // Select all permissions
    for (const permission of allPermissions) {
      this.selectedPermissionIds.add(permission.id);
    }
  }

  deselectAllPermissions(): void {
    // Get all permissions that match the current search term
    const term = this.permissionSearch.value.trim().toLowerCase();
    const allPermissions = this.permissionState.data().filter((permission) => {
      if (term) {
        return this.matchesSearch(permission, term);
      }
      return true;
    });

    // Deselect all permissions
    for (const permission of allPermissions) {
      this.selectedPermissionIds.delete(permission.id);
    }
  }

  async savePermissions(): Promise<void> {
    this.errorMessage = "";

    if (!this.roleId) {
      this.errorMessage = "Missing role identifier.";
      return;
    }

    this.isSaving = true;
    this.cdr.detectChanges();
    const permissionIds = Array.from(this.selectedPermissionIds);
    try {
      await this.roleEffects.setRolePermissions(this.roleId, permissionIds);
      setTimeout(() => {
        this.toastEffects.show("Role permissions updated", "success");
        this.cdr.detectChanges();
        this.router.navigate(["/roles"]).catch(() => undefined);
      }, 0);
    } catch (exception: unknown) {
      setTimeout(() => {
        this.errorMessage = getBackendErrorMessage(exception, "Unable to save permissions.");
        this.toastEffects.show(this.errorMessage, "error");
        this.cdr.detectChanges();
      }, 0);
    } finally {
      setTimeout(() => {
        this.isSaving = false;
        this.cdr.detectChanges();
      }, 0);
    }
  }

  goBack(): void {
    this.router.navigate(["/roles"]).catch(() => undefined);
  }

  private resolveRoleFromNavigation(): void {
    const state = this.router.getCurrentNavigation()?.extras?.state ?? history.state;
    const role = state?.["role"] as RoleListItem | undefined;

    if (!role) {
      this.router.navigate(["/roles"]).catch(() => undefined);
      return;
    }

    this.role = role;
    for (const permission of role.permissions ?? []) {
      this.selectedPermissionIds.add(permission.id);
    }
  }

  private matchesSearch(permission: Permission, term: string): boolean {
    return (
      permission.module.toLowerCase().includes(term) ||
      permission.action.toLowerCase().includes(term) ||
      (permission.description ?? "").toLowerCase().includes(term)
    );
  }

  private getModulePermissions(moduleName: string): Permission[] {
    return this.permissionState.data().filter((permission) => permission.module === moduleName);
  }
}
